import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getCanonicalUrl } from '@/lib/publicBaseUrl';
import { seedPost } from '@/data/seedBlogPost';
import { getCohortBySlug } from '@/lib/data/cohorts';

interface BreadcrumbItem {
  label: string;
  href: string;
}

export function GlobalBreadcrumbs() {
  const location = useLocation();
  const rawPath = location.pathname;

  // Strip locale prefix if present (e.g. /en-ZA/about -> /about)
  const pathname = rawPath.replace(/^\/(en[-_][a-z]{2})/i, '') || '/';

  // Do NOT show breadcrumbs on homepage
  if (pathname === '/') {
    return null;
  }

  const items: BreadcrumbItem[] = [{ label: 'Home', href: '/' }];

  if (pathname === '/practitioner-certification') {
    items.push({ label: 'Certification', href: '/cohorts' });
    items.push({ label: 'Practitioner Certification', href: '/practitioner-certification' });
  } else if (pathname === '/certification/team-facilitator') {
    items.push({ label: 'Certification', href: '/cohorts' });
    items.push({ label: 'Team Facilitator', href: '/certification/team-facilitator' });
  } else if (pathname === '/cohorts') {
    items.push({ label: 'Certification', href: '/cohorts' });
    items.push({ label: 'Upcoming Cohorts', href: '/cohorts' });
  } else if (pathname.startsWith('/cohorts/')) {
    items.push({ label: 'Certification', href: '/cohorts' });
    items.push({ label: 'Upcoming Cohorts', href: '/cohorts' });
    const slug = pathname.replace('/cohorts/', '');
    const cohort = getCohortBySlug(slug);
    const cohortLabel = cohort ? `${cohort.certificationName} (${cohort.monthYear})` : slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    items.push({ label: cohortLabel, href: pathname });
  } else if (pathname === '/icf-cce-points') {
    items.push({ label: 'Certification', href: '/cohorts' });
    items.push({ label: 'ICF CCE Points', href: '/icf-cce-points' });
  } else if (pathname === '/practitioners') {
    items.push({ label: 'Certification', href: '/cohorts' });
    items.push({ label: 'Practitioners', href: '/practitioners' });
  } else if (pathname === '/find-a-practitioner') {
    items.push({ label: 'Certification', href: '/cohorts' });
    items.push({ label: 'Find a Practitioner', href: '/find-a-practitioner' });
  } else if (pathname === '/framework') {
    items.push({ label: 'Framework', href: '/framework' });
  } else if (pathname === '/lens/enneagram') {
    items.push({ label: 'Framework', href: '/framework' });
    items.push({ label: 'The Enneagram', href: '/lens/enneagram' });
  } else if (pathname === '/lens/personal-mastery') {
    items.push({ label: 'Framework', href: '/framework' });
    items.push({ label: 'Personal Mastery', href: '/lens/personal-mastery' });
  } else if (pathname === '/lens/emotional-resilience') {
    items.push({ label: 'Framework', href: '/framework' });
    items.push({ label: 'Emotional Resilience', href: '/lens/emotional-resilience' });
  } else if (pathname === '/lens/social-drives') {
    items.push({ label: 'Framework', href: '/framework' });
    items.push({ label: 'Social Drives', href: '/lens/social-drives' });
  } else if (pathname === '/lens/energy-centres') {
    items.push({ label: 'Framework', href: '/framework' });
    items.push({ label: 'Energy Centers', href: '/lens/energy-centres' });
  } else if (pathname.startsWith('/enneagram-type-')) {
    items.push({ label: 'Framework', href: '/framework' });
    const typeMap: Record<string, string> = {
      '/enneagram-type-1': 'Enneagram Type 1: The Strict Perfectionist',
      '/enneagram-type-2': 'Enneagram Type 2: The Considerate Helper',
      '/enneagram-type-3': 'Enneagram Type 3: The Competitive Achiever',
      '/enneagram-type-4': 'Enneagram Type 4: The Intense Individualist',
      '/enneagram-type-5': 'Enneagram Type 5: The Quiet Specialist',
      '/enneagram-type-6': 'Enneagram Type 6: The Loyal Skeptic',
      '/enneagram-type-7': 'Enneagram Type 7: The Enthusiastic Visionary',
      '/enneagram-type-8': 'Enneagram Type 8: The Active Controller',
      '/enneagram-type-9': 'Enneagram Type 9: The Adaptive Peacemaker',
    };
    const label = typeMap[pathname] || `Enneagram Type ${pathname.replace('/enneagram-type-', '')}`;
    items.push({ label, href: pathname });
  } else if (pathname === '/organizations') {
    items.push({ label: 'Solutions', href: '/organizations' });
    items.push({ label: 'For Organisations', href: '/organizations' });
  } else if (pathname === '/team-effectiveness') {
    items.push({ label: 'Solutions', href: '/organizations' });
    items.push({ label: 'Team Effectiveness', href: '/team-effectiveness' });
  } else if (pathname === '/for-individuals') {
    items.push({ label: 'Solutions', href: '/organizations' });
    items.push({ label: 'For Individuals', href: '/for-individuals' });
  } else if (pathname === '/personal-mastery-and-resilience') {
    items.push({ label: 'Solutions', href: '/organizations' });
    items.push({ label: 'Personal Mastery', href: '/personal-mastery-and-resilience' });
  } else if (pathname === '/evidence-and-research') {
    items.push({ label: 'Evidence & Research', href: '/evidence-and-research' });
  } else if (pathname === '/blog') {
    items.push({ label: 'Blog', href: '/blog' });
  } else if (pathname.startsWith('/blog/')) {
    items.push({ label: 'Blog', href: '/blog' });
    const slug = pathname.replace('/blog/', '');
    let title = 'Article';
    if (slug === 'beginnings-endings-counterbalances' || slug === '1' || (seedPost && (seedPost as any).slug === slug)) {
      title = seedPost.title;
    } else {
      title = slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    if (slug) {
      items.push({ label: title, href: pathname });
    }
  } else if (pathname === '/about') {
    items.push({ label: 'About', href: '/about' });
  } else if (pathname === '/contact') {
    items.push({ label: 'Contact', href: '/contact' });
  } else if (pathname === '/diagnostic') {
    items.push({ label: 'Diagnostic', href: '/diagnostic' });
  } else if (pathname === '/mini-assessment') {
    items.push({ label: 'Free Mini Assessment', href: '/mini-assessment' });
  } else if (pathname === '/mini-assessment/results' || pathname === '/mini-assessment-results') {
    items.push({ label: 'Free Mini Assessment', href: '/mini-assessment' });
    items.push({ label: 'Results', href: pathname });
  } else if (pathname === '/pricing') {
    items.push({ label: 'Pricing', href: '/pricing' });
  } else if (pathname === '/resource-library') {
    items.push({ label: 'Resources', href: '/resource-library' });
  } else if (pathname.startsWith('/resource-library/')) {
    items.push({ label: 'Resources', href: '/resource-library' });
    const slug = pathname.replace('/resource-library/', '');
    let title = 'Article';
    if (slug === 'navigating-change-fatigue') {
      title = 'Navigating Change Fatigue';
    } else {
      title = slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    if (slug) {
      items.push({ label: title, href: pathname });
    }
  } else if (pathname === '/privacy-policy') {
    items.push({ label: 'Privacy Policy', href: '/privacy-policy' });
  } else if (pathname === '/terms-of-service') {
    items.push({ label: 'Terms of Service', href: '/terms-of-service' });
  } else if (pathname === '/register') {
    items.push({ label: 'Register', href: '/register' });
  } else {
    // Dynamic fallback for any other custom paths
    const segments = pathname.split('/').filter(Boolean);
    let currentPath = '';
    segments.forEach((segment) => {
      currentPath += `/${segment}`;
      const formattedLabel = segment.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      items.push({ label: formattedLabel, href: currentPath });
    });
  }

  // Construct BreadcrumbList JSON-LD Schema
  const schemaItems = items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.label,
    "item": getCanonicalUrl(item.href)
  }));

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": schemaItems
  };

  return (
    <div className="w-full bg-ground border-b border-ground-deep/60 pt-[82px] relative z-30">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-2.5">
        <nav aria-label="Breadcrumb" className="text-xs font-sans text-ink/60">
          <ol className="flex flex-wrap items-center gap-1">
            {items.map((item, index) => {
              const isLast = index === items.length - 1;
              return (
                <li key={index} className="flex items-center">
                  {index > 0 && (
                    <span className="mx-1.5 text-ink/30 font-light select-none" aria-hidden="true">
                      &gt;
                    </span>
                  )}
                  {isLast ? (
                    <span className="text-ink font-semibold" aria-current="page">
                      {item.label}
                    </span>
                  ) : (
                    <Link
                      to={item.href}
                      className="hover:text-rule-3 transition-colors underline-offset-2 hover:underline"
                    >
                      {item.label}
                    </Link>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      </div>
    </div>
  );
}
